import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import ExpenseModal from "../components/expenses/ExpenseModal";
import ScanModal from "../components/expenses/ScanModal";
import MoneyInModal from "../components/expenses/MoneyInModal";
import ExportModal from "../components/expenses/ExportModal";
import StatementsModal from "../components/expenses/StatementsModal";
import ReviewPanel from "../components/expenses/ReviewPanel";
import ExpenseCharts from "../components/expenses/ExpenseCharts";
import { getExpenses, bulkExpenses } from "../api/expenses";
import { formatMoney } from "../utils/money";
import { useLiveRefresh } from "../utils/live";
import { categoryIcon } from "../utils/expenseCategories";

// What the business has spent, on what, and who put the money in.

const usd0 = (c) => `${Number(c) < 0 ? "-" : ""}$${Math.abs(Math.round(Number(c || 0) / 100)).toLocaleString()}`;

// "2026-10-01" as a local date, so it never shifts a day.
function localDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}
const shortDay = (s) => localDate(s)?.toLocaleDateString([], { month: "short", day: "numeric" }) || "";
const monthKey = (s) => {
  const d = localDate(s);
  return d ? d.toLocaleDateString([], { month: "long", year: "numeric" }) : null;
};
const NO_DATE = "Date not known yet";

export default function AdminExpenses() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("spent");
  const [filter, setFilter] = useState(""); // a category, or "undated"
  const [editing, setEditing] = useState(null); // {id} | {initial}
  const [moneyIn, setMoneyIn] = useState(null); // {} new | entry
  const [scanning, setScanning] = useState(false);
  const [statementsOpen, setStatementsOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "charts" ? "charts" : "list";
  const setView = (v) => setParams(v === "charts" ? { view: "charts" } : {}, { replace: true });
  const actionsRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await getExpenses());
      setError("");
    } catch (e) {
      setError(e.message || "Couldn't load expenses.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(load);

  // The floating + appears once the quick actions scroll out of view (always, on the charts).
  useEffect(() => {
    const el = actionsRef.current;
    if (!el) {
      setShowFab(view === "charts");
      return undefined;
    }
    if (!("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => setShowFab(!e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [data, view]);

  const done = (changed) => {
    setEditing(null);
    setMoneyIn(null);
    if (changed) load();
  };

  const actions = {
    statements: () => setStatementsOpen(true),
    scan: () => setScanning(true),
    add: () => setEditing({ initial: { status: "paid" } }),
    moneyIn: () => setMoneyIn({}),
    toBuy: () => setEditing({ initial: { status: "planned" } }),
  };
  // On a phone the list sits below the summary, so jump to it to show the filter worked.
  const showList = () => {
    if (window.innerWidth < 1024) {
      setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  };
  const pickCategory = (c) => {
    setTab("spent");
    setFilter((f) => (f === c ? "" : c));
    showList();
  };
  const openReview = () => {
    setTab("review");
    showList();
  };

  return (
    <AppShell
      title="Expenses"
      wide
      action={
        data && (
          <Button variant="secondary" size="sm" onClick={() => setExporting(true)}>
            ⬇ Export
          </Button>
        )
      }
    >
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green flex justify-between gap-2">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice("")} className="text-birdie-green/60 min-h-0 min-w-0" aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}
      {data && <ViewSwitch view={view} onView={setView} />}
      {!data ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : view === "charts" ? (
        <div className="pb-16 lg:pb-0">
          <ExpenseCharts
            data={data}
            onOpenExpense={(id) => setEditing({ id })}
            onShowCategory={(c) => {
              setView("list");
              setTab("spent");
              setFilter(c);
              showList();
            }}
          />
        </div>
      ) : (
        <div className="lg:grid lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-5 lg:items-start">
          <aside className="space-y-3 lg:sticky lg:top-20">
            <Overview data={data} onReview={openReview} />
            <div ref={actionsRef} className="grid grid-cols-3 gap-2">
              <QuickAction icon="🏦" label="Statements" sub="bank, card, Amazon" onClick={actions.statements} primary />
              <QuickAction icon="📷" label="Receipt" sub="snap or upload" onClick={actions.scan} />
              <QuickAction icon="✍️" label="Add" sub="type it in" onClick={actions.add} />
            </div>
            <Breakdown data={data} active={filter} onPick={pickCategory} />
          </aside>

          {/* Bottom padding keeps the last row clear of the floating + button. */}
          <section ref={listRef} className="mt-4 lg:mt-0 space-y-3 min-w-0 pb-16 lg:pb-0 scroll-mt-20">
            <Tabs
              tab={tab}
              onTab={setTab}
              counts={{
                review: data.review_count || 0,
                spent: data.summary.paid_count,
                in: data.money_in.length,
                tobuy: data.summary.planned_count,
              }}
            />
            {tab === "review" && <ReviewPanel data={data} onChanged={load} onOpenExpense={(id) => setEditing({ id })} />}
            {tab === "spent" && (
              <SpentList
                data={data}
                filter={filter}
                onFilter={setFilter}
                onOpen={(id) => setEditing({ id })}
                onStatements={actions.statements}
                selecting={selecting}
                onSelecting={setSelecting}
                onBulk={async (ids, change, del) => {
                  const r = await bulkExpenses(ids, change, del);
                  setData(r);
                  return r.updated;
                }}
              />
            )}
            {tab === "in" && <MoneyInList data={data} onOpen={(m) => setMoneyIn(m)} onAdd={actions.moneyIn} />}
            {tab === "tobuy" && <ToBuyList data={data} onOpen={(id) => setEditing({ id })} onAdd={actions.toBuy} />}
          </section>
        </div>
      )}

      {data && showFab && !(selecting && tab === "spent") && (
        <button
          type="button"
          onClick={() => setSheet(true)}
          aria-label="Add"
          className="fixed right-4 bottom-24 sm:bottom-8 z-30 h-14 w-14 rounded-full bg-birdie-green text-white text-3xl leading-none shadow-lg shadow-birdie-green/30 flex items-center justify-center"
        >
          +
        </button>
      )}
      {sheet && <ActionSheet onClose={() => setSheet(false)} actions={actions} />}
      {exporting && data && <ExportModal years={data.export_years || []} onClose={() => setExporting(false)} />}

      {editing && data && (
        <ExpenseModal expenseId={editing.id || null} initial={editing.initial} data={data} onClose={done} />
      )}
      {moneyIn && data && <MoneyInModal entry={moneyIn.id ? moneyIn : null} data={data} onClose={done} />}
      {statementsOpen && data && (
        <StatementsModal
          data={data}
          onClose={(changed, result) => {
            setStatementsOpen(false);
            if (changed) {
              setNotice(sortedNotice(result));
              setTab("review");
              load();
            }
          }}
        />
      )}
      {scanning && data && (
        <ScanModal
          data={data}
          onClose={(changed, added) => {
            setScanning(false);
            if (changed) {
              const bits = [
                added?.expenses && `${added.expenses} expense${added.expenses === 1 ? "" : "s"}`,
                added?.money_in && `${added.money_in} deposit${added.money_in === 1 ? "" : "s"}`,
              ].filter(Boolean);
              setNotice(bits.length ? `Added ${bits.join(" and ")}.` : "Done.");
              load();
            }
          }}
        />
      )}
    </AppShell>
  );
}

function ViewSwitch({ view, onView }) {
  return (
    <div className="mb-3 inline-flex rounded-xl bg-birdie-cream p-1" role="group" aria-label="View">
      {[
        { key: "list", label: "📋 List" },
        { key: "charts", label: "📊 Charts" },
      ].map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={() => onView(v.key)}
          aria-pressed={view === v.key}
          className={`min-h-0 px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
            view === v.key ? "bg-white text-birdie-charcoal shadow-sm" : "text-birdie-charcoal/55"
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

function sortedNotice(r) {
  if (!r) return "Done.";
  const n = (k, one, many) => (r[k] ? `${r[k]} ${r[k] === 1 ? one : many}` : null);
  const handled = [
    n("matched_amazon", "Amazon order matched to its card charge", "Amazon orders matched to their card charges"),
    n("duplicates", "line you already had", "lines you already had"),
    n("skipped", "transfer or card payment skipped", "transfers and card payments skipped"),
  ].filter(Boolean);
  return [
    `Read ${r.lines} line${r.lines === 1 ? "" : "s"}.`,
    handled.length ? `${handled.join(", ")}.` : "",
    r.to_review ? `${r.to_review} to go through below.` : "Nothing new to add.",
    r.note || "",
  ]
    .filter(Boolean)
    .join(" ");
}

function Overview({ data, onReview }) {
  const s = data.summary;
  const now = new Date();
  const thisMonth = data.expenses
    .filter((e) => e.status === "paid" && e.spent_on)
    .filter((e) => {
      const d = localDate(e.spent_on);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    })
    .reduce((t, e) => t + Number(e.amount_cents), 0);
  const maxIn = Math.max(1, ...s.partners.map((p) => p.total));
  const unfunded = s.company_paid - s.deposits;

  return (
    <div className="rounded-2xl overflow-hidden border border-birdie-green/15 bg-white">
      <div className="bg-birdie-green text-white px-4 pt-4 pb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-white/70">Total spent</div>
            <div className="text-[2rem] leading-tight font-semibold tabular-nums">{formatMoney(s.spent)}</div>
            <div className="text-[11px] text-white/65 mt-0.5">
              {s.paid_count} expense{s.paid_count === 1 ? "" : "s"}
              {s.planned_count > 0 && ` · ${s.planned_count} still to buy (~${usd0(s.planned)})`}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-white/70">This month</div>
            <div className="text-lg font-semibold tabular-nums">{usd0(thisMonth)}</div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-2.5">
        <div className="text-xs font-semibold text-birdie-charcoal/60">Who put money in</div>
        {s.partners.map((p) => (
          <div key={p.partner}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-birdie-charcoal">{p.partner}</span>
              <span className="text-sm font-semibold tabular-nums text-birdie-charcoal">{formatMoney(p.total)}</span>
            </div>
            <div className="h-2 rounded-full bg-birdie-cream mt-1" role="presentation">
              <div className="h-full rounded-full bg-birdie-green" style={{ width: `${Math.max(1.5, (p.total / maxIn) * 100)}%` }} />
            </div>
            <div className="text-[11px] text-birdie-charcoal/50 mt-0.5">
              {usd0(p.deposits)} deposited · {usd0(p.paid_personally)} on own cards
            </div>
          </div>
        ))}
        {/* A small gap is normal: sales deposits get spent too, and the card is paid off after the fact.
            A big one usually means a deposit isn't recorded yet. */}
        {unfunded > 0 && (
          <p className="text-[11px] text-birdie-charcoal/55 pt-1 border-t border-birdie-green/10">
            {unfunded > s.company_paid * 0.1 ? (
              <>
                Company accounts paid {usd0(s.company_paid)}, but only {usd0(s.deposits)} of deposits are recorded. Add your
                bank statements to show who funded the other {usd0(unfunded)}.
              </>
            ) : (
              <>
                Company accounts paid {usd0(s.company_paid)}; {s.partners.map((p) => p.partner).join(" and ")} deposited{" "}
                {usd0(s.deposits)}. The other {usd0(unfunded)} is customer payments that were spent, or card charges not
                paid off yet.
              </>
            )}
          </p>
        )}
        {data.review_count > 0 && (
          <button
            type="button"
            onClick={onReview}
            className="w-full text-left text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2"
          >
            {data.review_count} thing{data.review_count === 1 ? " needs" : "s need"} your input ›
          </button>
        )}
      </div>
    </div>
  );
}

function QuickAction({ icon, label, sub, onClick, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl px-2 py-3 text-center border transition ${
        primary
          ? "bg-birdie-mustard/20 border-birdie-mustard/50 hover:bg-birdie-mustard/30"
          : "bg-white border-birdie-green/15 hover:bg-birdie-cream"
      }`}
    >
      <span className="block text-2xl leading-none" aria-hidden>
        {icon}
      </span>
      <span className="block text-sm font-semibold text-birdie-charcoal mt-1.5">{label}</span>
      <span className="block text-[10px] leading-tight text-birdie-charcoal/50">{sub}</span>
    </button>
  );
}

function Breakdown({ data, active, onPick }) {
  const [all, setAll] = useState(false);
  const cats = data.summary.by_category;
  if (!cats.length) return null;
  const total = Math.max(1, cats.reduce((t, c) => t + Math.max(0, Number(c.total)), 0));
  const max = Math.max(1, ...cats.map((c) => Number(c.total)));
  const top = typeof window !== "undefined" && window.innerWidth < 1024 ? 3 : 5;
  const shown = all ? cats : cats.slice(0, top);
  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-accent text-base text-birdie-charcoal">Where it went</h2>
        <span className="text-[11px] text-birdie-charcoal/45">tap to filter</span>
      </div>
      <ul className="space-y-1">
        {shown.map((c) => {
          const on = active === c.category;
          return (
            <li key={c.category}>
              <button
                type="button"
                onClick={() => onPick(c.category)}
                className={`w-full text-left rounded-lg px-2 py-1.5 -mx-2 ${on ? "bg-birdie-green/10" : "hover:bg-birdie-cream/70"}`}
                aria-pressed={on}
              >
                <div className="flex items-center gap-2 text-sm">
                  <span aria-hidden>{categoryIcon(c.category)}</span>
                  <span className="min-w-0 flex-1 truncate text-birdie-charcoal">{c.category}</span>
                  <span className="text-[11px] text-birdie-charcoal/45 tabular-nums">{Math.round((Number(c.total) / total) * 100)}%</span>
                  <span className="w-20 text-right tabular-nums font-medium text-birdie-charcoal">{usd0(c.total)}</span>
                </div>
                <div className="ml-6 h-1.5 rounded-full bg-birdie-cream mt-1">
                  <div className="h-full rounded-full bg-birdie-green/80" style={{ width: `${Math.max(1.5, (Number(c.total) / max) * 100)}%` }} />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {cats.length > top && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-1 text-xs font-semibold text-birdie-green">
          {all ? "Show less" : `Show all ${cats.length}`}
        </button>
      )}
    </div>
  );
}

function Tabs({ tab, onTab, counts }) {
  const tabs = [
    ...(counts.review || tab === "review" ? [{ key: "review", label: "Review" }] : []),
    { key: "spent", label: "Spent" },
    { key: "in", label: "Money in" },
    { key: "tobuy", label: "To buy" },
  ];
  return (
    <div className="flex gap-1 rounded-xl bg-birdie-cream p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onTab(t.key)}
          className={`flex-1 min-h-0 px-1.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
            tab === t.key ? "bg-white text-birdie-charcoal shadow-sm" : "text-birdie-charcoal/55"
          }`}
        >
          {t.label}
          {t.key === "review" ? (
            counts.review > 0 && (
              <span className="ml-1 inline-block min-w-[1.25rem] rounded-full bg-amber-500 px-1 text-[11px] leading-5 text-white">
                {counts.review}
              </span>
            )
          ) : (
            <span className={`ml-1 text-xs ${tab === t.key ? "text-birdie-charcoal/45" : "text-birdie-charcoal/35"}`}>{counts[t.key]}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function SpentList({ data, filter, onFilter, onOpen, onStatements, selecting, onSelecting, onBulk }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const paid = useMemo(() => data.expenses.filter((e) => e.status === "paid"), [data.expenses]);
  const undated = paid.filter((e) => !e.spent_on).length;
  const usedCats = data.summary.by_category.map((c) => c.category);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return paid.filter(
      (e) =>
        (!filter || (filter === "undated" ? !e.spent_on : e.category === filter)) &&
        (!term || [e.description, e.vendor, e.notes, e.account_name, e.category].some((x) => (x || "").toLowerCase().includes(term)))
    );
  }, [paid, q, filter]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const e of rows) {
      const k = monthKey(e.spent_on) || NO_DATE;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    const list = [...m.entries()];
    // Undated last, however they sort.
    return [...list.filter(([k]) => k !== NO_DATE), ...list.filter(([k]) => k === NO_DATE)];
  }, [rows]);

  // Selections only ever cover what's on screen, so a filter change drops the rest.
  useEffect(() => {
    setSel((s) => {
      const shown = new Set(rows.map((e) => e.id));
      const kept = [...s].filter((id) => shown.has(id));
      return kept.length === s.size ? s : new Set(kept);
    });
  }, [rows]);

  const toggle = (ids, on) =>
    setSel((s) => {
      const n = new Set(s);
      for (const id of ids) {
        if (on) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  const stopSelecting = () => {
    setSel(new Set());
    onSelecting(false);
  };

  if (!paid.length) {
    return (
      <div className="bg-white border border-birdie-green/15 rounded-2xl p-8 text-center">
        <div className="text-4xl">🧾</div>
        <div className="font-accent text-lg text-birdie-charcoal mt-2">No expenses yet</div>
        <p className="text-sm text-birdie-charcoal/60 mt-1">Add your bank and card statements and the AI sorts them for you.</p>
        <Button onClick={onStatements} className="mt-4">
          🏦 Add statements
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-birdie-charcoal/35" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path strokeLinecap="round" d="m20 20-3.5-3.5" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search expenses"
            aria-label="Search expenses"
            className="w-full rounded-xl border border-birdie-green/20 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
          />
        </div>
        <button
          type="button"
          onClick={() => (selecting ? stopSelecting() : onSelecting(true))}
          className={`shrink-0 rounded-xl border px-3 text-sm font-semibold ${
            selecting ? "bg-birdie-green text-white border-birdie-green" : "bg-white text-birdie-charcoal/70 border-birdie-green/20"
          }`}
        >
          {selecting ? "Done" : "Select"}
        </button>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 lg:mx-0 lg:px-0 [scrollbar-width:none]">
        <Chip on={!filter} onClick={() => onFilter("")}>
          All
        </Chip>
        {undated > 0 && (
          <Chip on={filter === "undated"} onClick={() => onFilter(filter === "undated" ? "" : "undated")} warn>
            Needs a date · {undated}
          </Chip>
        )}
        {usedCats.map((c) => (
          <Chip key={c} on={filter === c} onClick={() => onFilter(filter === c ? "" : c)}>
            {categoryIcon(c)} {c}
          </Chip>
        ))}
      </div>
      {selecting && (
        <div className="flex items-center justify-between gap-2 px-1 text-xs text-birdie-charcoal/60">
          <span>Tap expenses to select them, or a month to select the whole month.</span>
          <button
            type="button"
            onClick={() => toggle(rows.map((e) => e.id), sel.size < rows.length)}
            className="shrink-0 font-semibold text-birdie-green"
          >
            {sel.size < rows.length ? `Select all ${rows.length}` : "Clear"}
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <p className="text-sm text-birdie-charcoal/55 text-center py-8">Nothing matches.</p>
      ) : (
        groups.map(([month, list]) => {
          const allOn = list.every((e) => sel.has(e.id));
          const head = (
            <>
              <span className="text-xs font-semibold text-birdie-charcoal/70">
                {selecting && <Tick on={allOn} />}
                {month}
              </span>
              <span className="text-xs font-semibold tabular-nums text-birdie-charcoal/70">
                {formatMoney(list.reduce((t, e) => t + Number(e.amount_cents), 0))}
              </span>
            </>
          );
          return (
            <div key={month} className="bg-white border border-birdie-green/15 rounded-2xl overflow-hidden">
              {selecting ? (
                <button
                  type="button"
                  onClick={() => toggle(list.map((e) => e.id), !allOn)}
                  className="w-full flex justify-between items-center px-4 py-2 bg-birdie-cream/60 border-b border-birdie-green/10 text-left"
                >
                  {head}
                </button>
              ) : (
                <div className="flex justify-between items-baseline px-4 py-2 bg-birdie-cream/60 border-b border-birdie-green/10">{head}</div>
              )}
              <ul className="divide-y divide-birdie-green/10">
                {list.map((e) => (
                  <li key={e.id}>
                    <ExpenseRow
                      e={e}
                      showDate={month !== NO_DATE}
                      selected={selecting ? sel.has(e.id) : null}
                      onOpen={() => (selecting ? toggle([e.id], !sel.has(e.id)) : onOpen(e.id))}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
      <p className="text-[11px] text-center text-birdie-charcoal/40">
        Cards and accounts are in <Link to="/admin/settings" className="underline">Settings</Link>.
      </p>
      {selecting && <BulkBar data={data} ids={[...sel]} onBulk={onBulk} onDone={() => setSel(new Set())} />}
      {/* Room for the bar, so it never covers the last expense. */}
      {selecting && <div className="h-24" aria-hidden />}
    </div>
  );
}

function Tick({ on }) {
  return (
    <span
      className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full border align-[-3px] text-[10px] ${
        on ? "bg-birdie-green border-birdie-green text-white" : "bg-white border-birdie-charcoal/30"
      }`}
      aria-hidden
    >
      {on ? "✓" : ""}
    </span>
  );
}

// The change you pick here goes to every selected expense at once.
function BulkBar({ data, ids, onBulk, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(""); // category | card | date
  const n = ids.length;

  async function apply(change, del = false) {
    if (!n) return;
    if (del && !window.confirm(`Delete ${n} expense${n === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBusy(true);
    setMsg("");
    try {
      const done = await onBulk(ids, change, del);
      setMsg(`${del ? "Deleted" : "Updated"} ${done} expense${done === 1 ? "" : "s"}.`);
      setOpen("");
      onDone();
    } catch (e) {
      setMsg(e.message || "Couldn't do that.");
    } finally {
      setBusy(false);
    }
  }

  const toggle = (k) => setOpen((o) => (o === k ? "" : k));
  const chip = "min-h-0 rounded-full border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/20";
  return (
    <div className="fixed z-30 inset-x-2 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] sm:bottom-6 sm:left-1/2 sm:right-auto sm:w-[34rem] sm:-translate-x-1/2">
      <div className={`rounded-2xl bg-birdie-charcoal text-white shadow-xl p-2.5 ${busy ? "opacity-70 pointer-events-none" : ""}`}>
        {open && n > 0 && (
          <div className="mb-2.5 max-h-56 overflow-y-auto rounded-xl bg-white/5 p-2">
            {open === "category" && (
              <div className="flex flex-wrap gap-1.5">
                {data.categories.map((c) => (
                  <button key={c} type="button" onClick={() => apply({ category: c })} className={chip}>
                    {categoryIcon(c)} {c}
                  </button>
                ))}
              </div>
            )}
            {open === "card" && (
              <div className="flex flex-wrap gap-1.5">
                {data.accounts
                  .filter((x) => !x.archived)
                  .map((x) => (
                    <button key={x.id} type="button" onClick={() => apply({ account_id: x.id })} className={chip}>
                      {x.kind === "card" ? "💳" : "🏦"} {x.name}
                      {x.owner !== "company" ? ` (${x.owner}'s)` : ""}
                    </button>
                  ))}
              </div>
            )}
            {open === "date" && (
              <label className="flex items-center gap-2 text-xs">
                <span className="text-white/70">Set the date to</span>
                <input
                  type="date"
                  onChange={(e) => e.target.value && apply({ spent_on: e.target.value })}
                  className="min-h-0 rounded-lg bg-white px-2 py-1 text-birdie-charcoal"
                  aria-label="Date"
                />
              </label>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 px-1 text-xs">
          <span className="font-semibold">{n ? `${n} selected` : "Tap expenses to select them"}</span>
          {msg && <span className="min-w-0 truncate text-white/60">{msg}</span>}
        </div>
        <div className={`grid grid-cols-4 gap-1.5 mt-2 ${n ? "" : "opacity-40 pointer-events-none"}`}>
          {[
            ["category", "🏷", "Category"],
            ["card", "💳", "Card"],
            ["date", "📅", "Date"],
          ].map(([k, icon, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => toggle(k)}
              aria-expanded={open === k}
              className={`min-h-0 rounded-xl px-1 py-1.5 text-[11px] font-semibold ${open === k ? "bg-white text-birdie-charcoal" : "bg-white/10"}`}
            >
              <span className="block text-base leading-tight" aria-hidden>
                {icon}
              </span>
              {label}
            </button>
          ))}
          <button type="button" onClick={() => apply({}, true)} className="min-h-0 rounded-xl bg-white/10 px-1 py-1.5 text-[11px] font-semibold text-red-200">
            <span className="block text-base leading-tight" aria-hidden>
              🗑
            </span>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ExpenseRow({ e, showDate, onOpen, selected = null }) {
  const refund = Number(e.amount_cents) < 0;
  const personal = e.account_owner && e.account_owner !== "company";
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-pressed={selected === null ? undefined : selected}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left ${selected ? "bg-birdie-green/10" : "hover:bg-birdie-cream/50"}`}
    >
      <span
        className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-lg ${
          selected ? "bg-birdie-green text-white" : "bg-birdie-cream"
        }`}
        aria-hidden
      >
        {selected ? "✓" : categoryIcon(e.category)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-birdie-charcoal truncate">
          {e.description}
          {Number(e.files) > 0 && (
            <span className="ml-1.5 text-[11px] font-normal text-birdie-charcoal/40" title="Has a receipt">
              📎
            </span>
          )}
        </span>
        <span className="block text-xs text-birdie-charcoal/50 truncate">
          {[showDate && shortDay(e.spent_on), selected !== null && e.category, e.vendor, e.account_name || "Which card?"]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className={`block text-sm font-semibold tabular-nums ${refund ? "text-birdie-green" : "text-birdie-charcoal"}`}>
          {refund ? "+" : ""}
          {formatMoney(Math.abs(Number(e.amount_cents)))}
        </span>
        {personal && (
          <span className="inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-800">
            {e.account_owner} paid
          </span>
        )}
        {refund && <span className="block text-[10px] text-birdie-green">refund</span>}
      </span>
    </button>
  );
}

function Chip({ on, onClick, children, warn }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 min-h-0 rounded-full px-3 py-1.5 text-xs font-semibold border whitespace-nowrap transition ${
        on
          ? "bg-birdie-green text-white border-birdie-green"
          : warn
            ? "bg-amber-50 text-amber-800 border-amber-200"
            : "bg-white text-birdie-charcoal/70 border-birdie-green/15"
      }`}
    >
      {children}
    </button>
  );
}

function MoneyInList({ data, onOpen, onAdd }) {
  const s = data.summary;
  return (
    <div className="space-y-3">
      <div className="bg-white border border-birdie-green/15 rounded-2xl p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-birdie-charcoal/60">
            Personal money moved into company accounts. Anything paid on a personal card already counts, so it doesn't go here.
          </p>
          <Button size="sm" onClick={onAdd} className="shrink-0">
            + Add
          </Button>
        </div>
        <div className="flex gap-2 mt-3">
          {s.partners.map((p) => (
            <div key={p.partner} className="flex-1 rounded-xl bg-birdie-cream/70 px-3 py-2">
              <div className="text-[11px] text-birdie-charcoal/55">{p.partner} deposited</div>
              <div className="text-base font-semibold tabular-nums text-birdie-charcoal">{formatMoney(p.deposits)}</div>
            </div>
          ))}
        </div>
      </div>
      {data.money_in.length === 0 ? (
        <p className="text-sm text-birdie-charcoal/55 text-center py-6">
          Nothing yet. Scanning a bank statement picks deposits up automatically.
        </p>
      ) : (
        <ul className="bg-white border border-birdie-green/15 rounded-2xl divide-y divide-birdie-green/10 overflow-hidden">
          {data.money_in.map((m) => (
            <li key={m.id}>
              <button type="button" onClick={() => onOpen(m)} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-birdie-cream/50">
                <span className="h-10 w-10 shrink-0 rounded-full bg-blue-50 text-blue-800 flex items-center justify-center text-sm font-bold" aria-hidden>
                  {m.partner.slice(0, 1)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-birdie-charcoal">
                    {m.partner}
                    {m.account_name && <span className="font-normal text-birdie-charcoal/55"> → {m.account_name}</span>}
                  </span>
                  {m.note && <span className="block text-xs text-birdie-charcoal/55 line-clamp-2">{m.note}</span>}
                  {m.paid_for?.length > 0 && (
                    <span className="block text-[11px] text-birdie-green mt-0.5">Paid for: {m.paid_for.map((x) => x.description).join(", ")}</span>
                  )}
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-sm font-semibold tabular-nums text-birdie-charcoal">{formatMoney(m.amount_cents)}</span>
                  <span className="block text-[11px] text-birdie-charcoal/45">{m.received_on ? shortDay(m.received_on) : "No date"}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ToBuyList({ data, onOpen, onAdd }) {
  const items = data.expenses.filter((e) => e.status === "planned");
  const known = items.reduce((t, e) => t + Number(e.amount_cents || 0), 0);
  const unknown = items.filter((e) => !Number(e.amount_cents)).length;
  return (
    <div className="space-y-3">
      <div className="bg-white border border-birdie-green/15 rounded-2xl p-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-birdie-charcoal">
            {items.length} item{items.length === 1 ? "" : "s"} · about {usd0(known)}
          </div>
          {unknown > 0 && <div className="text-[11px] text-birdie-charcoal/50">plus {unknown} without a price yet</div>}
        </div>
        <Button size="sm" onClick={onAdd}>
          + Add
        </Button>
      </div>
      {items.length > 0 && (
        <ul className="bg-white border border-birdie-green/15 rounded-2xl divide-y divide-birdie-green/10 overflow-hidden">
          {items.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => onOpen(e.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-birdie-cream/50">
                <span className="h-10 w-10 shrink-0 rounded-full bg-birdie-cream flex items-center justify-center text-lg" aria-hidden>
                  {categoryIcon(e.category)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-birdie-charcoal truncate">{e.description}</span>
                  <span className="block text-xs text-birdie-charcoal/50 truncate">{[e.category, e.notes].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="shrink-0 text-sm tabular-nums text-birdie-charcoal/70">
                  {Number(e.amount_cents) ? `~${usd0(e.amount_cents)}` : "Price?"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 && <p className="text-[11px] text-center text-birdie-charcoal/40">Bought something? Open it and tap “Bought it”.</p>}
    </div>
  );
}

function ActionSheet({ onClose, actions }) {
  const run = (fn) => () => {
    onClose();
    fn();
  };
  const items = [
    { icon: "🏦", label: "Add statements", sub: "Bank, card or Amazon. The AI sorts them and asks you", fn: actions.statements },
    { icon: "📷", label: "Scan a receipt", sub: "The AI reads it; you check it", fn: actions.scan },
    { icon: "✍️", label: "Add an expense", sub: "Type it in", fn: actions.add },
    { icon: "💵", label: "Money in", sub: "Tyler or Gabe put money into the business", fn: actions.moneyIn },
    { icon: "📝", label: "Add to the to-buy list", sub: "Something you still need", fn: actions.toBuy },
  ];
  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-birdie-charcoal/40" />
      <div
        className="absolute bottom-0 inset-x-0 sm:inset-auto sm:right-6 sm:bottom-24 sm:w-80 bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-2 pb-[calc(env(safe-area-inset-bottom)+8px)]"
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((i) => (
          <button key={i.label} type="button" onClick={run(i.fn)} className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-birdie-cream">
            <span className="text-2xl" aria-hidden>
              {i.icon}
            </span>
            <span>
              <span className="block text-sm font-semibold text-birdie-charcoal">{i.label}</span>
              <span className="block text-xs text-birdie-charcoal/55">{i.sub}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
